import { AfterViewInit, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { COUNTRIES_DATA } from '../../../../fixtures/fixtures';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, switchMap, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { TranslocoModule } from '@jsverse/transloco';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import { SubjectEntityService } from '../services/subject.entity.service';
import { MatDialog } from '@angular/material/dialog';
import { SubjectEntity } from '../subject.entity';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { map } from 'rxjs/operators';
import { getMinFilterLengthText } from '../../core/util';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { CommonModule } from '@angular/common';
import { FormDialogSubjectComponent } from '../form-dialog-subject/form-dialog-subject.component';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalStorageService } from '../../core/services/local-storage.service';

@Component({
  selector: 'pixl-main-subject-management',
  imports: [
    CommonModule,
    MatIcon,
    MatSortModule,
    MatIconButton,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    TranslocoModule,
    FreeFilterInputComponent,
    MatPaginator
  ],
  templateUrl: './main-subject.component.html',
  styleUrl: './main-subject.component.scss'
})
export class MainSubjectComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private subjectService = inject(SubjectEntityService);
  private dialog = inject(MatDialog);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private localStorageService = inject(LocalStorageService);

  subjects$!: Observable<SubjectEntity[]>;
  countries = COUNTRIES_DATA;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  public readonly filterLen = 3;
  private nameFilter = '';
  private regionFilter: string[] = [];
  public currentSelectedRow = signal<string | undefined>(undefined);
  public displayedColumns: string[] = ['actions', 'id', 'name', 'region'];
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 25;
  pageIndex = 0;
  totalItems = 1000;

  private destroyRef = inject(DestroyRef);
  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: this.pageIndex, pageSize: this.pageSize });

  ngOnInit() {
    this.subjects$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': 'name'
        };

        if (this.nameFilter?.trim()?.length >= this.filterLen) {
          query['page[number]'] = 0;
          query['filter[nameLike]'] = this.nameFilter.trim();
        }

        if (this.regionFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[region]'] = this.regionFilter.join(',');
        }

        return from(this.subjectService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.subjectService.allByQuery(query))),
          map((subjects: SubjectEntity[]) => subjects.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.subjectService.stateClass) as EntityStateModel<SubjectEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit() {
    this.paginator.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  add(): void {
    const dialogRef = this.dialog.open(FormDialogSubjectComponent, {
      width: '500px',
      maxWidth: 'none',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<SubjectEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new SubjectEntity(), result);
        await this.subjectService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: 'Fach erfolgreich erstellt',
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  edit(subject: SubjectEntity) {
    this.currentSelectedRow.set(subject.id);
    const dialogRef = this.dialog.open(FormDialogSubjectComponent, {
      width: '500px',
      maxWidth: 'none',
      data: { ...subject }
    });

    dialogRef.afterClosed().subscribe(async (updatedSubject: SubjectEntity | undefined) => {
      if (!updatedSubject) return;
      await firstValueFrom(this.subjectService.update(updatedSubject));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: 'Fach erfolgreich editiert',
        messageTye: 'mtSuccess'
      });
    });
  }

  async delete(subject: SubjectEntity) {
    this.currentSelectedRow.set(subject.id);
    if (!subject?.id) {
      console.error('ID is undefined!', subject);
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(subject.name),
            headerText: 'Fach löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.subjectService.delete(subject.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Fach erfolgreich gelöscht.',
      messageTye: 'mtSuccess'
    });
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.filterLen);
  }

  public setNameFilter(input: string): void {
    this.currentSelectedRow.set(undefined);
    this.nameFilter = input;
    this.refresh$.next();
  }

  public setRegionFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined);
    this.regionFilter = selected;
    this.refresh$.next();
  }

  trackById(index: number, subject: SubjectEntity): string {
    return subject.id;
  }
}
